import React from 'react';
import { Typography, Tooltip, useTheme, CircularProgress } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useStyles } from './DiagnosticSummary.style';
import OtherLesions from './OtherLesions';
import { Skeleton } from '@material-ui/lab';

// New component for BI-RADS category lesions with Calc/Opacities buttons
const BiradsCategoryLesions = ({
  mergedLesionsByCategory,
  otherLesions,
  onMouseEnter,
  onMouseLeave,
  onClick,
  emptyFindings,
  isLoading = false,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  const biradsCategories = ['birads2', 'birads3', 'birads4', 'birads5'];

  return (
    <div
      className={classes.diagnostic_sections_container}
      style={{ justifyContent: 'space-evenly', height: '55%', marginBottom: '2%' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}>
        <Typography className={classes.diagnostic_table_title}>
          {t('ResultView.diagnosticsFindings')}
        </Typography>
      </div>

      <div className={classes.lesions_main_container}>
        {/* Vessels and Known Mass Chips */}
        {otherLesions?.length > 0 && (
          <OtherLesions
            lesions={otherLesions}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={onClick}
          />
        )}

        <div className={classes.lesions_container}>
          {isLoading ? (
            // Show loading state
            <Skeleton variant='rect' width='60%' height={12} />
          ) : emptyFindings ? (
            <Tooltip
              arrow
              placement='bottom'
              title={<Typography className={classes.tooltip_text}>{t('report.no_lesion')}</Typography>}>
              <div className={classes.empty_chip_container}>
                <Typography className={classes.diagnostic_birads_label}>{t('revision.birads1')}</Typography>
              </div>
            </Tooltip>
          ) : (
            biradsCategories.map((category) => {
              const categoryData = mergedLesionsByCategory[category];
              const microcalcLesion = categoryData?.microcalc;
              const opacitiesLesion = categoryData?.opacities;

              return (
                <div key={category} className={classes.birads_category_container}>
                  <Typography className={classes.birads_category_label}>
                    {t(`revision.${category}`).toUpperCase()}
                  </Typography>

                  <div className={classes.lesion_buttons_container}>
                    {/* Calc button */}
                    <Tooltip
                      arrow
                      placement='bottom'
                      title={
                        <Typography className={classes.tooltip_text}>
                          {microcalcLesion?.tooltip_title}
                        </Typography>
                      }>
                      <div
                        className={classes.lesion_button}
                        style={{
                          backgroundColor: microcalcLesion?.overlaysExist
                            ? microcalcLesion?.isShown
                              ? `${microcalcLesion.color}70`
                              : `${microcalcLesion.color}20`
                            : theme.palette.grey[700],
                          borderRadius: '5px 0 0 5px',
                          cursor: microcalcLesion?.overlaysExist ? 'pointer' : 'default',
                          opacity: microcalcLesion?.overlaysExist ? 1 : 0.5,
                          border: microcalcLesion?.overlaysExist
                            ? microcalcLesion?.isShown
                              ? `1px solid ${microcalcLesion.color}50`
                              : `1px solid ${theme.palette.grey[700]}`
                            : `1px solid ${theme.palette.grey[600]}`,
                          color: microcalcLesion?.overlaysExist
                            ? microcalcLesion?.isShown
                              ? 'white'
                              : microcalcLesion.color
                            : 'gray',
                          fontWeight: microcalcLesion?.isShown ? 'bold' : 'unset',
                        }}
                        onMouseEnter={() =>
                          onMouseEnter(
                            'calc',
                            microcalcLesion.overlaysExist,
                            microcalcLesion.isShown,
                            microcalcLesion.overlaysToShow
                          )
                        }
                        onMouseLeave={() => onMouseLeave('calc', microcalcLesion.overlaysToShow)}
                        onClick={() =>
                          onClick(
                            'calc',
                            microcalcLesion.overlaysToShow,
                            microcalcLesion.isShown,
                            microcalcLesion.overlaysExist
                          )
                        }>
                        {t('diagnose.calc')}
                      </div>
                    </Tooltip>

                    {/* Opacities button */}
                    <Tooltip
                      arrow
                      placement='bottom'
                      title={
                        <Typography className={classes.tooltip_text}>
                          {opacitiesLesion?.tooltip_title || t('diagnose.opacities')}
                        </Typography>
                      }>
                      <div
                        className={classes.lesion_button}
                        style={{
                          backgroundColor: opacitiesLesion?.overlaysExist
                            ? opacitiesLesion?.isShown
                              ? `${opacitiesLesion.color}70`
                              : `${opacitiesLesion.color}20`
                            : theme.palette.grey[700],
                          borderRadius: '0 5px 5px 0',
                          cursor: opacitiesLesion?.overlaysExist ? 'pointer' : 'default',
                          opacity: opacitiesLesion?.overlaysExist ? 1 : 0.5,
                          border: opacitiesLesion?.overlaysExist
                            ? opacitiesLesion?.isShown
                              ? `1px solid ${opacitiesLesion.color}50`
                              : `1px solid ${theme.palette.grey[700]}`
                            : `1px solid ${theme.palette.grey[600]}`,
                          color: opacitiesLesion?.overlaysExist
                            ? opacitiesLesion?.isShown
                              ? 'white'
                              : opacitiesLesion.color
                            : 'gray',
                          fontWeight: opacitiesLesion?.isShown ? 'bold' : 'unset',
                        }}
                        onMouseEnter={() =>
                          onMouseEnter(
                            'opac',
                            opacitiesLesion.overlaysExist,
                            opacitiesLesion.isShown,
                            opacitiesLesion.overlaysToShow
                          )
                        }
                        onMouseLeave={() => onMouseLeave('opac', opacitiesLesion.overlaysToShow)}
                        onClick={() =>
                          onClick(
                            'opac',
                            opacitiesLesion.overlaysToShow,
                            opacitiesLesion.isShown,
                            opacitiesLesion.overlaysExist
                          )
                        }>
                        {t('diagnose.opac')}
                      </div>
                    </Tooltip>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default BiradsCategoryLesions;
